import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  Clock,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Target,
  Award,
  Bot,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookConsultation = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    serviceInterest: "",
    preferredTime: "",
    message: "",
  });

  const timeSlots = [
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
  ];

  const serviceOptions = [
    "Online Marketing Package ($500/mo)",
    "Reputation Management ($200/mo)",
    "Custom Enterprise Solution",
    "Not sure - need consultation",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !date || !formData.preferredTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/consultations`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service_interest: formData.serviceInterest || "Online Marketing Package",
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: formData.preferredTime,
        message: formData.message,
      });

      toast.success(response.data.message);
      navigate("/?consultation_booked=true");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book consultation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="book-consultation-page" className="pt-20 min-h-screen bg-muted/30">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {/* Back Button */}
        <Link to="/pricing" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Pricing
        </Link>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-3">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle style={{ fontFamily: "Outfit, sans-serif" }}>
                  Book Your Consultation
                </CardTitle>
                <p className="text-muted-foreground">
                  Schedule a call with our team to discuss your marketing goals.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="consultation-name-input"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="consultation-email-input"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="consultation-phone-input"
                        placeholder="(123) 456-7890"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        data-testid="consultation-company-input"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Service Interest */}
                  <div className="space-y-2">
                    <Label htmlFor="serviceInterest">What service are you interested in?</Label>
                    <Select
                      value={formData.serviceInterest}
                      onValueChange={(value) => setFormData({ ...formData, serviceInterest: value })}
                    >
                      <SelectTrigger data-testid="consultation-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date & Time Selection */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="consultation-date-picker"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(date) => 
                              date < new Date() || 
                              date.getDay() === 0 || 
                              date.getDay() === 6
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="preferredTime">Preferred Time *</Label>
                      <Select
                        value={formData.preferredTime}
                        onValueChange={(value) => setFormData({ ...formData, preferredTime: value })}
                      >
                        <SelectTrigger data-testid="consultation-time-select">
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time} EST
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">Tell us about your goals (optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="consultation-message-input"
                      placeholder="What are your main reputation or marketing challenges? What would you like to achieve?"
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="consultation-submit-btn"
                    className="w-full bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full py-6 text-lg font-semibold"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Booking...
                      </>
                    ) : (
                      <>
                        <CalendarIcon className="mr-2 h-5 w-5" />
                        Book Consultation
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    You'll receive a confirmation email with calendar invite.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* What to Expect */}
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
                  What to Expect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  "30-minute strategy session",
                  "Review of your current online presence",
                  "Custom recommendations for your business",
                  "Transparent pricing discussion",
                  "No obligation to purchase",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#F37021] flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Online Marketing Package Info */}
            <Card className="bg-[#F37021]/10 border-[#F37021]/20">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="h-8 w-8 text-[#F37021]" />
                  <div>
                    <p className="font-bold text-foreground">Online Marketing Package</p>
                    <p className="text-sm text-muted-foreground">$500/month</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Our premium package includes everything you need to dominate your online presence:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Award className="h-4 w-4 text-[#F37021]" />
                    <span>Google Platinum Partner services</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Bot className="h-4 w-4 text-[#F37021]" />
                    <span>AI A/B testing (5000+ parameters)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-[#F37021]" />
                    <span>24/7 reputation monitoring</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <p className="text-sm text-muted-foreground mb-2">
                  Prefer to talk now?
                </p>
                <a 
                  href="tel:+1234567890" 
                  className="text-lg font-bold text-[#F37021] hover:underline"
                >
                  (123) 456-7890
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultation;
